/**
 * Reading a dataset zip in the browser.
 *
 * The bytes never leave the user's machine until they confirm a plan, so the
 * whole "what is in this file?" question is answered client-side. `jszip` is
 * already a dependency (the detection exporter writes zips with it); this is
 * the first place that reads one.
 *
 * Three things here are load-bearing and look like over-engineering until they
 * bite:
 *
 * - **`JSZip.loadAsync` synthesises directory entries.** A zip holding only
 *   `a/cat/1.jpg` yields FOUR `forEach` callbacks — `a/`, `a/cat/`, the file,
 *   and on a macOS-made archive `__MACOSX/` too. Counting those as images
 *   inflates every number on the card, so `entry.dir` is filtered first.
 *
 * - **macOS archives carry `__MACOSX/._name.jpg` stubs.** They are four bytes
 *   of AppleDouble metadata with an image extension. PIL cannot open one, and
 *   the backend's uploader has no per-file try/except — a single bad file loses
 *   every image in that background task. They are dropped here.
 *
 * - **A split folder is not a class.** `train/cat/1.jpg` and `val/cat/1.jpg`
 *   are the same class photographed twice, and a naive "last folder wins" reads
 *   `train` and `val` as the class names for a two-class dataset that is really
 *   one class. Split segments are stripped before the class is taken.
 *
 * `analyzeEntries` is deliberately PURE over `[{path, size}]` — no JSZip handle,
 * no File, no browser — so the interesting logic is testable in jest without
 * building a zip at all.
 */

import { detectAnnotationFormat } from './datasetAnnotations';

/**
 * Extensions the backend can store AND thumbnail.
 *
 * Narrower than "what PIL can open", on purpose. `save_files` picks a thumbnail
 * format from a map of these four and falls back to JPEG for anything else — so
 * an RGBA `.bmp` would reach `Image.save(format="JPEG")`, raise, and take the
 * whole upload task down with it. Anything outside this list is reported as
 * skipped rather than silently shipped.
 */
export const IMAGE_EXTENSIONS = Object.freeze(['jpg', 'jpeg', 'png', 'webp']);
const EXTENSION_SET = new Set(IMAGE_EXTENSIONS);

/** Folder names that describe a dataset SPLIT rather than a class. */
export const SPLIT_FOLDERS = Object.freeze([
  'train', 'training', 'val', 'valid', 'validation', 'test', 'testing', 'eval', 'evaluation',
]);
const SPLIT_SET = new Set(SPLIT_FOLDERS);

/**
 * Caps on one import.
 *
 * There is exactly one `save_classification_data` request per build and it
 * cannot be chunked — re-saving a version HARD-deletes its data items first, so
 * a second chunk would destroy the first. Starlette also refuses a multipart
 * body with more than 1000 parts. The card states these before the user
 * consents rather than failing halfway.
 */
export const MAX_IMPORT_IMAGES = 300;
export const MAX_IMPORT_BYTES = 150 * 1024 * 1024;

/** Images sent to `/cluster/auto` in one request. */
export const MAX_CLUSTER_SAMPLE = 240;

/** A folder layout needs at least this many class dirs holding this share. */
const MIN_CLASS_DIRS = 2;
const CLASS_COVERAGE = 0.8;

const segmentsOf = (path) => String(path || '').split('/').filter(Boolean);

/**
 * Archive noise: resource forks, dotfiles and Windows thumbnail caches.
 *
 * Checked on every segment, not just the basename — `__MACOSX` is a top-level
 * directory whose children have perfectly ordinary-looking names.
 */
export const isJunkPath = (path) => {
  const segments = segmentsOf(path);
  if (!segments.length) return true;
  return segments.some(
    (segment) =>
      segment === '__MACOSX' ||
      segment.startsWith('.') ||
      segment.toLowerCase() === 'thumbs.db',
  );
};

export const extensionOf = (name) => {
  const text = String(name || '');
  const dot = text.lastIndexOf('.');
  return dot < 0 ? '' : text.slice(dot + 1).toLowerCase();
};

export const isImagePath = (path) => EXTENSION_SET.has(extensionOf(path));

/**
 * The class a path belongs to, or null when it sits at the root.
 *
 * The last directory segment that is not a split name. `a/train/cat/1.jpg` and
 * `a/val/cat/1.jpg` both answer `cat`; `train/1.jpg` answers null, because
 * stripping the split leaves nothing.
 */
export const classSegmentFor = (path) => {
  const segments = segmentsOf(path);
  const directories = segments
    .slice(0, -1)
    .filter((segment) => !SPLIT_SET.has(segment.toLowerCase()));
  return directories.length ? directories[directories.length - 1] : null;
};

/** True when any segment of the path names a split. */
export const hasSplitSegment = (path) =>
  segmentsOf(path)
    .slice(0, -1)
    .some((segment) => SPLIT_SET.has(segment.toLowerCase()));

/**
 * Turn raw zip entries into everything the plan and the card need.
 *
 * Pure: `entries` is `[{path, size}]` and nothing else is touched.
 */
/** Files that describe a dataset rather than being one. */
const SIDECAR_EXTENSIONS = new Set(['txt', 'yaml', 'yml', 'json', 'names', 'csv']);

export const analyzeEntries = (entries = []) => {
  const images = [];
  const sidecars = [];
  const paths = [];
  const skipped = { junk: 0, nonImage: 0 };
  let splitFoldersDetected = false;

  (entries || []).forEach((entry) => {
    const path = entry && entry.path;
    if (!path) return;
    if (isJunkPath(path)) {
      skipped.junk += 1;
      return;
    }
    paths.push(path);
    if (!isImagePath(path)) {
      // A .txt/.yaml/.json is not a broken image — it is very likely the
      // annotations. Counting it as "skipped" would tell the user their
      // detection dataset was half-unreadable.
      if (SIDECAR_EXTENSIONS.has(extensionOf(path))) sidecars.push(path);
      else skipped.nonImage += 1;
      return;
    }
    if (hasSplitSegment(path)) splitFoldersDetected = true;
    const segments = segmentsOf(path);
    images.push({
      path,
      name: segments[segments.length - 1],
      folder: classSegmentFor(path),
      size: Number(entry.size) || 0,
    });
  });

  const byFolder = new Map();
  images.forEach((image) => {
    const key = image.folder || '';
    if (!byFolder.has(key)) byFolder.set(key, []);
    byFolder.get(key).push(image);
  });

  const namedFolders = [...byFolder.entries()].filter(([name]) => name !== '');
  const inNamedFolders = namedFolders.reduce((sum, [, list]) => sum + list.length, 0);

  let layout = 'flat';
  if (!images.length) {
    layout = 'empty';
  } else if (
    namedFolders.length >= MIN_CLASS_DIRS &&
    inNamedFolders >= images.length * CLASS_COVERAGE
  ) {
    layout = 'folder_per_class';
  }

  const groups = namedFolders
    .map(([name, list]) => ({
      name,
      count: list.length,
      bytes: list.reduce((sum, image) => sum + image.size, 0),
      sample: list.slice(0, 3).map((image) => image.name),
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  // Basenames that repeat inside one class. Both handlers key their metadata by
  // name, so two `001.jpg` in one class collapse to a single map entry: one
  // MinIO object, one row, one image lost with nothing reported. The payload
  // builder renames them; this is what tells the user it happened.
  let duplicateBasenames = 0;
  const scopes = layout === 'folder_per_class' ? byFolder : new Map([['', images]]);
  scopes.forEach((list) => {
    const seen = new Set();
    list.forEach((image) => {
      const key = image.name.toLowerCase();
      if (seen.has(key)) duplicateBasenames += 1;
      else seen.add(key);
    });
  });

  const totalBytes = images.reduce((sum, image) => sum + image.size, 0);

  // Detection annotations, if this archive carries any. Path-only detection:
  // no file is read until the user confirms a plan.
  const annotation = detectAnnotationFormat(paths);

  return {
    layout,
    images,
    groups,
    sidecars,
    paths,
    annotation,
    totalImages: images.length,
    totalBytes,
    skipped,
    splitFoldersDetected,
    duplicateBasenames,
    overImageCap: images.length > MAX_IMPORT_IMAGES,
    overByteCap: totalBytes > MAX_IMPORT_BYTES,
  };
};

/**
 * Take an evenly spaced sample of at most `limit` items.
 *
 * Even rather than "the first N" so an over-cap folder dataset keeps every
 * class instead of importing the alphabetically-first two and dropping the rest.
 */
export const evenSample = (items = [], limit = 0) => {
  const list = items || [];
  if (limit <= 0 || list.length <= limit) return [...list];
  const stride = list.length / limit;
  const picked = [];
  for (let index = 0; index < limit; index += 1) {
    picked.push(list[Math.floor(index * stride)]);
  }
  return picked;
};

/**
 * Read a zip with JSZip and analyse it.
 *
 * `JSZip` is injected so the test can drive this without the import, and so the
 * caller owns the one dynamic import rather than this module pulling a ~100KB
 * dependency into the assistant's initial chunk.
 *
 * `_data.uncompressedSize` is a private field. It is read defensively and used
 * only for display and the size cap — never for a correctness branch — because
 * the alternative is decompressing every entry just to measure it.
 */
export const scanZip = async (file, JSZip) => {
  const zip = await JSZip.loadAsync(file);
  const entries = [];
  zip.forEach((path, entry) => {
    if (entry.dir) return;
    entries.push({ path, size: (entry._data && entry._data.uncompressedSize) || 0 });
  });
  return { zip, analysis: analyzeEntries(entries) };
};

/** Pull one entry's bytes out of an already-loaded zip. */
export const readEntryBlob = async (zip, path) => {
  const entry = zip && zip.file(path);
  if (!entry) throw new Error(`Missing from the archive: ${path}`);
  return entry.async('blob');
};

/** One entry's bytes, for reading an image header. */
export const readEntryBytes = async (zip, path) => {
  const entry = zip && zip.file(path);
  if (!entry) throw new Error(`Missing from the archive: ${path}`);
  return entry.async('uint8array');
};

/** One entry as text — label files, data.yaml, classes.txt. */
export const readEntryText = async (zip, path) => {
  const entry = zip && zip.file(path);
  if (!entry) throw new Error(`Missing from the archive: ${path}`);
  return entry.async('string');
};

/** "12.4 MB" — for the card, so a user can sanity-check before consenting. */
export const formatBytes = (bytes) => {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  const units = ['KB', 'MB', 'GB'];
  let scaled = value / 1024;
  let unit = 0;
  while (scaled >= 1024 && unit < units.length - 1) {
    scaled /= 1024;
    unit += 1;
  }
  // One decimal up to 100 — "12.4 MB" is a size a user can judge, "12 MB" hides
  // a 400KB difference that matters against a 150MB cap.
  return `${scaled < 100 ? scaled.toFixed(1) : Math.round(scaled)} ${units[unit]}`;
};
